/**
 * DecisionFlow — PositionPlanAssignmentWorkspace
 * ui/src/components/admin/PositionPlanAssignmentWorkspace.jsx
 *
 * Spec authority: comp_policies_spec_v2_3.md §16
 * Session: S613 · OI-ASSIGNMENT-WORKSPACE-BUILD (UI revision post smoke test)
 *
 * Two-tab surface:
 *   Tab A — Position Assignment  (shuttle UI)
 *   Tab B — CSV Import           (existing dry-run → commit pattern)
 *
 * Position Assignment layout:
 *   Row 1 (full width): Assignment Details bar
 *     BU · Plan · Effective Date · End Date (opt) · Preview button
 *     Component (read-only auto-resolved) · Period (read-only locked from shell)
 *   Row 2 (two panes + shuttle controls):
 *     Left  — Positions (manager-grouped, checkboxes, search, filters)
 *     Centre — >> / << shuttle buttons (stacked)
 *     Right  — Selected positions (checkboxes, search, clear all)
 *
 * Interaction model:
 *   Checkbox/row click = select only, nothing moves
 *   >> moves all left-checked to right, clears left checkboxes
 *   << moves all right-checked back to left, clears right checkboxes
 *   >> disabled when nothing checked left; << disabled when nothing checked right
 *   Preview enabled: right pane >= 1 + BU + Plan + effective_date
 *   Amber indicator on positions with existing active assignment (both panes)
 *   Post-confirm: right clears, left restored, BU+Plan retained
 *
 * CSV Import tab: choose file → validate (dry_run=true) → commit (dry_run=false)
 * Consistent with quota/credit/position import surfaces.
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind. Zero dark: variants.
 *      No form tags. No localStorage/sessionStorage.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users, Search, AlertTriangle, CheckCircle,
  Loader2, ChevronDown, Info, Upload,
  ChevronRight, ChevronLeft, FileUp, X,
} from "lucide-react";

const API_BASE = "/api/v1";

const TABS = [
  { key: "shuttle", label: "Assign by Position", icon: Users  },
  { key: "csv",     label: "Assign by CSV",      icon: FileUp },
];

export default function PositionPlanAssignmentWorkspace({ period }) {
  const [activeTab, setActiveTab] = useState("shuttle");

  const fiscalPeriodId   = period?.fiscal_period_id ?? null;
  const fiscalPeriodCode = period?.period_code       ?? null;

  const [positions,           setPositions]           = useState([]);
  const [businessUnits,       setBusinessUnits]       = useState([]);
  const [approvedPlans,       setApprovedPlans]       = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [loadingData,         setLoadingData]         = useState(true);
  const [loadError,           setLoadError]           = useState(null);

  const loadAll = useCallback(async () => {
    setLoadingData(true);
    setLoadError(null);
    try {
      const [posRes, buRes, planRes] = await Promise.all([
        fetch(`${API_BASE}/positions`,                  { credentials: "include" }),
        fetch(`${API_BASE}/business-units`,             { credentials: "include" }),
        fetch(`${API_BASE}/plans?plan_status=approved`, { credentials: "include" }),
      ]);
      if (!posRes.ok)  throw new Error(`Positions: ${posRes.status}`);
      if (!buRes.ok)   throw new Error(`Business units: ${buRes.status}`);
      if (!planRes.ok) throw new Error(`Plans: ${planRes.status}`);
      const [posData, buData, planData] = await Promise.all([
        posRes.json(), buRes.json(), planRes.json(),
      ]);
      setPositions(posData?.data      ?? posData?.positions      ?? []);
      setBusinessUnits(buData?.data   ?? buData?.business_units  ?? []);
      setApprovedPlans(planData?.data ?? planData?.plans         ?? []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoadingData(false);
    }
  }, []);

  const loadExistingAssignments = useCallback(async () => {
    if (!fiscalPeriodId) return;
    try {
      const r = await fetch(
        `${API_BASE}/plan-assignments?fiscal_period_id=${fiscalPeriodId}`,
        { credentials: "include" }
      );
      if (!r.ok) return;
      const d = await r.json();
      setExistingAssignments(d?.data ?? []);
    } catch (_) {}
  }, [fiscalPeriodId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadExistingAssignments(); }, [loadExistingAssignments]);

  const periodLabel = period
    ? `${period.period_code ?? ""} · ${period.fiscal_year_label ?? "FY2026"} · ${period.period_status ?? "open"}`
    : "No period selected";

  const plansForPeriod = useMemo(() => {
    if (!fiscalPeriodId) return [];
    return approvedPlans.filter((p) => p.fiscal_period_id === fiscalPeriodId);
  }, [approvedPlans, fiscalPeriodId]);

  const positionById = useMemo(() => {
    const m = {};
    positions.forEach((p) => { m[p.position_id] = p; });
    return m;
  }, [positions]);

  const assignedIds = useMemo(
    () => new Set(existingAssignments.map((a) => a.position_id)),
    [existingAssignments]
  );

  if (loadError) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
        Failed to load workspace: {loadError}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">

      {/* Tab bar */}
      <div className="flex items-center border-b border-[rgb(var(--color-border))] px-4 pt-3 gap-1">
        {TABS.map((tab) => {
          const Icon   = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-tok-sm font-medium rounded-t-lg border-b-2 transition ${
                active
                  ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                  : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "shuttle" && (
        <ShuttleTab
          period={period}
          periodLabel={periodLabel}
          fiscalPeriodCode={fiscalPeriodCode}
          positions={positions}
          businessUnits={businessUnits}
          plansForPeriod={plansForPeriod}
          positionById={positionById}
          assignedIds={assignedIds}
          loadingData={loadingData}
          onAssignmentComplete={loadExistingAssignments}
        />
      )}
      {activeTab === "csv" && (
        <CsvImportTab onAssignmentComplete={loadExistingAssignments} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShuttleTab
// ---------------------------------------------------------------------------
function ShuttleTab({
  period, periodLabel, fiscalPeriodCode,
  positions, businessUnits, plansForPeriod, positionById, assignedIds,
  loadingData, onAssignmentComplete,
}) {
  const [selectedBuId,     setSelectedBuId]     = useState("");
  const [selectedPlanCode, setSelectedPlanCode] = useState("");
  const [effectiveDate,    setEffectiveDate]     = useState("");
  const [endDate,          setEndDate]           = useState("");

  const [leftSearch,  setLeftSearch]  = useState("");
  const [groupBy,     setGroupBy]     = useState("manager");
  const [activeOnly,  setActiveOnly]  = useState(true);
  const [roleFilter,  setRoleFilter]  = useState("");
  const [leftChecked, setLeftChecked] = useState(new Set());

  const [rightItems,   setRightItems]   = useState([]);
  const [rightSearch,  setRightSearch]  = useState("");
  const [rightChecked, setRightChecked] = useState(new Set());

  const [showPreview,    setShowPreview]    = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult,  setPreviewResult]  = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [toast,          setToast]          = useState(null);

  useEffect(() => {
    if (!effectiveDate && period?.period_start_date) setEffectiveDate(period.period_start_date);
  }, [period, effectiveDate]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const rightIds = useMemo(() => new Set(rightItems.map((p) => p.position_id)), [rightItems]);

  const availableRoles = useMemo(() => {
    const seen = new Map();
    positions.forEach((p) => {
      if (p.role_code && !seen.has(p.role_code)) seen.set(p.role_code, p.role_name ?? p.role_code);
    });
    return [...seen.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [positions]);

  const selectedPlan = useMemo(
    () => plansForPeriod.find((p) => p.plan_code === selectedPlanCode) ?? null,
    [plansForPeriod, selectedPlanCode]
  );

  const leftFiltered = useMemo(() => {
    let ps = positions.filter((p) => !rightIds.has(p.position_id));
    if (activeOnly) ps = ps.filter((p) => p.is_active);
    if (roleFilter) ps = ps.filter((p) => p.role_code === roleFilter);
    if (leftSearch.trim()) {
      const q = leftSearch.toLowerCase();
      ps = ps.filter((p) => p.full_name?.toLowerCase().includes(q) || p.employee_id?.toLowerCase().includes(q));
    }
    return ps;
  }, [positions, rightIds, activeOnly, roleFilter, leftSearch]);

  const leftGrouped = useMemo(() => {
    if (groupBy === "flat") {
      return [{ key: "__all__", label: null, items: [...leftFiltered].sort((a, b) => a.full_name.localeCompare(b.full_name)) }];
    }
    const groups = {};
    leftFiltered.forEach((p) => {
      let key, label;
      if (groupBy === "manager") {
        const mgr = p.manager_position_id ? positionById[p.manager_position_id] : null;
        key   = mgr?.position_id ?? "__no_manager__";
        label = mgr?.full_name   ?? "No Manager";
      } else {
        key   = p.role_code ?? "__unknown__";
        label = p.role_name ?? p.role_code ?? "Unknown Role";
      }
      if (!groups[key]) groups[key] = { key, label, items: [] };
      groups[key].items.push(p);
    });
    return Object.values(groups).sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
  }, [leftFiltered, groupBy, positionById]);

  const rightFiltered = useMemo(() => {
    if (!rightSearch.trim()) return rightItems;
    const q = rightSearch.toLowerCase();
    return rightItems.filter((p) => p.full_name?.toLowerCase().includes(q) || p.employee_id?.toLowerCase().includes(q));
  }, [rightItems, rightSearch]);

  const previewEnabled = rightItems.length > 0 && selectedBuId && selectedPlanCode && effectiveDate;

  const moveRight = useCallback(() => {
    if (leftChecked.size === 0) return;
    const toMove = leftFiltered.filter((p) => leftChecked.has(p.position_id));
    setRightItems((prev) => {
      const existingIds = new Set(prev.map((p) => p.position_id));
      return [...prev, ...toMove.filter((p) => !existingIds.has(p.position_id))];
    });
    setLeftChecked(new Set());
  }, [leftChecked, leftFiltered]);

  const moveLeft = useCallback(() => {
    if (rightChecked.size === 0) return;
    setRightItems((prev) => prev.filter((p) => !rightChecked.has(p.position_id)));
    setRightChecked(new Set());
  }, [rightChecked]);

  const toggleLeftCheck = useCallback((id) => {
    setLeftChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleRightCheck = useCallback((id) => {
    setRightChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleGroupCheck = useCallback((group) => {
    const ids = group.items.map((p) => p.position_id);
    const allChecked = ids.every((id) => leftChecked.has(id));
    setLeftChecked((prev) => {
      const next = new Set(prev);
      allChecked ? ids.forEach((id) => next.delete(id)) : ids.forEach((id) => next.add(id));
      return next;
    });
  }, [leftChecked]);

  const clearRight = useCallback(() => { setRightItems([]); setRightChecked(new Set()); }, []);

  const buildCsv = useCallback(() => {
    const bu = businessUnits.find((b) => String(b.business_unit_id) === String(selectedBuId));
    if (!bu || !selectedPlan || !fiscalPeriodCode) return null;
    const header = "employee_id,plan_code,fiscal_period_code,business_unit_code,effective_date,end_date\n";
    return header + rightItems.map((p) =>
      `${p.employee_id},${selectedPlan.plan_code},${fiscalPeriodCode},${bu.bu_code},${effectiveDate},${endDate}`
    ).join("\n") + "\n";
  }, [rightItems, selectedBuId, selectedPlan, fiscalPeriodCode, businessUnits, effectiveDate, endDate]);

  const handlePreview = useCallback(async () => {
    const csv = buildCsv();
    if (!csv) return;
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const fd = new FormData();
      fd.append("file", new Blob([csv], { type: "text/csv" }), "PlanAssignments.csv");
      const r = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=true&force=true`, { method: "POST", credentials: "include", body: fd });
      setPreviewResult(await r.json());
      setShowPreview(true);
    } catch (e) {
      setToast({ type: "error", message: `Preview failed: ${e.message}` });
    } finally {
      setPreviewLoading(false);
    }
  }, [buildCsv]);

  const handleConfirm = useCallback(async () => {
    const csv = buildCsv();
    if (!csv) return;
    setConfirmLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", new Blob([csv], { type: "text/csv" }), "PlanAssignments.csv");
      const r = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=false&force=true`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json();
      if (d.assignments_created > 0 || !d.errors?.length) {
        const sup = d.assignments_superseded > 0 ? ` ${d.assignments_superseded} superseded.` : "";
        setToast({ type: "success", message: `${d.assignments_created} assignment${d.assignments_created === 1 ? "" : "s"} created.${sup}` });
        setRightItems([]);
        setRightChecked(new Set());
        setLeftChecked(new Set());
        setShowPreview(false);
        setPreviewResult(null);
        await onAssignmentComplete();
      } else {
        setToast({ type: "error", message: d.errors?.[0]?.message ?? "Assignment failed." });
      }
    } catch (e) {
      setToast({ type: "error", message: `Commit failed: ${e.message}` });
    } finally {
      setConfirmLoading(false);
      setShowPreview(false);
    }
  }, [buildCsv, onAssignmentComplete]);

  return (
    <div className="p-4 space-y-3">

      {/* Row 1 — Assignment Details (full width) */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]/40 p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* BU */}
          <div className="space-y-1">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Business Unit</label>
            <div className="relative">
              <select value={selectedBuId} onChange={(e) => setSelectedBuId(e.target.value)}
                className="w-full appearance-none rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 pr-7 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] cursor-pointer">
                <option value="">Select BU…</option>
                {businessUnits.map((bu) => <option key={bu.business_unit_id} value={bu.business_unit_id}>{bu.bu_name} ({bu.bu_code})</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            </div>
          </div>
          {/* Plan */}
          <div className="space-y-1">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Plan</label>
            <div className="relative">
              <select value={selectedPlanCode} onChange={(e) => setSelectedPlanCode(e.target.value)}
                className="w-full appearance-none rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 pr-7 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] cursor-pointer">
                <option value="">Select Plan…</option>
                {plansForPeriod.map((pl) => <option key={pl.plan_id} value={pl.plan_code}>{pl.plan_name} · {pl.role_code}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            </div>
            {!loadingData && plansForPeriod.length === 0 && period && (
              <p className="text-tok-xs text-[rgb(var(--color-warning))]">No approved plans for this period.</p>
            )}
          </div>
          {/* Effective Date */}
          <div className="space-y-1">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Effective Date <span className="text-[rgb(var(--color-danger))]">*</span></label>
            <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]" />
          </div>
          {/* End Date */}
          <div className="space-y-1">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text))]">End Date <span className="text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(optional)</span></label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]" />
          </div>
        </div>
        {/* Component + Period + Preview */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span className="flex items-center gap-1"><Info className="h-3.5 w-3.5 shrink-0" />Component: auto-resolved from plan + position role</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Period: {periodLabel}</span>
          </div>
          <button onClick={handlePreview} disabled={!previewEnabled || previewLoading}
            title={!previewEnabled ? `${rightItems.length === 0 ? "Select positions. " : ""}${!selectedBuId ? "Select BU. " : ""}${!selectedPlanCode ? "Select plan. " : ""}${!effectiveDate ? "Set effective date." : ""}` : undefined}
            className="shrink-0 flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-5 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-40 disabled:cursor-not-allowed transition">
            {previewLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Checking…</> : "Preview Assignment"}
          </button>
        </div>
      </div>

      {/* Row 2 — Shuttle panes */}
      <div className="flex rounded-xl border border-[rgb(var(--color-border))] overflow-hidden" style={{ minHeight: 420 }}>

        {/* Left pane */}
        <div className="flex flex-col border-r border-[rgb(var(--color-border))]" style={{ width: "46%" }}>
          <div className="px-3 pt-3 pb-2 border-b border-[rgb(var(--color-border))] space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Positions</p>
              {leftChecked.size > 0 && <span className="text-tok-xs text-[rgb(var(--color-primary))]">{leftChecked.size} checked</span>}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input type="text" placeholder="Search name or ID…" value={leftSearch} onChange={(e) => setLeftSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-8 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                  className="appearance-none rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-2.5 pr-6 py-1 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none cursor-pointer">
                  <option value="manager">By Manager</option>
                  <option value="role">By Role</option>
                  <option value="flat">Flat List</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-[rgb(var(--color-text-muted))]" />
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[rgb(var(--color-primary))]" />
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Active only</span>
              </label>
              {availableRoles.length > 0 && (
                <div className="relative">
                  <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
                    className="appearance-none rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-2.5 pr-6 py-1 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none cursor-pointer">
                    <option value="">All roles</option>
                    {availableRoles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-[rgb(var(--color-text-muted))]" />
                </div>
              )}
            </div>
          </div>
          <div className="overflow-y-auto" style={{maxHeight:360}}>
            {loadingData ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[rgb(var(--color-text-muted))]" /></div>
            ) : leftGrouped.length === 0 ? (
              <p className="px-3 py-4 text-tok-xs text-[rgb(var(--color-text-muted))]">{positions.length === 0 ? "No positions found." : "All positions moved to selected."}</p>
            ) : (
              leftGrouped.map((group) => (
                <div key={group.key}>
                  {group.label && (
                    <div className="flex items-center gap-2 px-3 py-1.5 sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                      <input type="checkbox"
                        checked={group.items.length > 0 && group.items.every((p) => leftChecked.has(p.position_id))}
                        onChange={() => toggleGroupCheck(group)}
                        className="h-3.5 w-3.5 accent-[rgb(var(--color-primary))]" />
                      <span className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] flex-1 truncate">{group.label}</span>
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{group.items.length}</span>
                    </div>
                  )}
                  {group.items.map((pos) => (
                    <PositionRow key={pos.position_id} pos={pos}
                      checked={leftChecked.has(pos.position_id)}
                      onCheck={() => toggleLeftCheck(pos.position_id)}
                      hasAssignment={assignedIds.has(pos.position_id)} />
                  ))}
                </div>
              ))
            )}
          </div>
          <div className="border-t border-[rgb(var(--color-border))] px-3 py-2">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {leftFiltered.length} available · {positions.filter((p) => p.is_active).length} active total
            </span>
          </div>
        </div>

        {/* Shuttle controls */}
        <div className="flex flex-col items-center justify-center gap-2 px-2 bg-[rgb(var(--color-surface-muted))]/30">
          <button onClick={moveRight} disabled={leftChecked.size === 0} title="Move checked to selected"
            className="flex items-center justify-center h-8 w-8 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/10 disabled:opacity-30 disabled:cursor-not-allowed transition">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button onClick={moveLeft} disabled={rightChecked.size === 0} title="Return checked to positions"
            className="flex items-center justify-center h-8 w-8 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-30 disabled:cursor-not-allowed transition">
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        {/* Right pane */}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="px-3 pt-3 pb-2 border-b border-[rgb(var(--color-border))] space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Selected</p>
              {rightChecked.size > 0 && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rightChecked.size} checked</span>}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              <input type="text" placeholder="Search…" value={rightSearch} onChange={(e) => setRightSearch(e.target.value)}
                className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] pl-8 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]" />
            </div>
          </div>
          <div className="overflow-y-auto" style={{maxHeight:360}}>
            {rightFiltered.length === 0 ? (
              <p className="px-3 py-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {rightItems.length === 0 ? "Check positions on the left and click › to add." : "No matches."}
              </p>
            ) : (
              rightFiltered.map((pos) => (
                <PositionRow key={pos.position_id} pos={pos}
                  checked={rightChecked.has(pos.position_id)}
                  onCheck={() => toggleRightCheck(pos.position_id)}
                  hasAssignment={assignedIds.has(pos.position_id)} />
              ))
            )}
          </div>
          <div className="border-t border-[rgb(var(--color-border))] px-3 py-2 flex items-center justify-between">
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{rightItems.length} selected</span>
            {rightItems.length > 0 && (
              <button onClick={clearRight} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-danger))] transition">Clear all</button>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-tok-sm font-medium border ${
          toast.type === "success"
            ? "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]"
            : "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]"
        }`}>
          {toast.type === "success" ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          {toast.message}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewResult && (
        <PreviewModal result={previewResult} onBack={() => setShowPreview(false)}
          onConfirm={handleConfirm} confirmLoading={confirmLoading}
          positionCount={rightItems.length} planName={selectedPlan?.plan_name ?? selectedPlanCode}
          periodLabel={period?.period_code ?? ""} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PositionRow — shared
// ---------------------------------------------------------------------------
function PositionRow({ pos, checked, onCheck, hasAssignment }) {
  return (
    <div
      className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] ${checked ? "bg-[rgb(var(--color-primary))]/5" : ""}`}
      onClick={onCheck}
    >
      <input type="checkbox" checked={checked} onChange={onCheck} onClick={(e) => e.stopPropagation()}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[rgb(var(--color-primary))]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-tok-xs font-medium text-[rgb(var(--color-text))] truncate">{pos.full_name}</span>
          {hasAssignment && <AlertTriangle className="h-3 w-3 shrink-0 text-[rgb(var(--color-warning))]" title="Has active assignment this period" />}
        </div>
        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          {[pos.role_code, pos.segment_code, pos.employee_id].filter(Boolean).join(" · ")}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CsvImportTab
// ---------------------------------------------------------------------------
function CsvImportTab({ onAssignmentComplete }) {
  const [csvFile,   setCsvFile]   = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [importing, setImporting] = useState(false);
  const [csvError,  setCsvError]  = useState("");
  const [toast,     setToast]     = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const runDryRun = useCallback(async (file) => {
    if (!file) return;
    setImporting(true); setCsvError(""); setDryResult(null); setCommitted(false);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=true&force=true`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json();
      if (!r.ok) { setCsvError(d?.detail?.error?.message ?? d?.detail ?? `Error ${r.status}`); return; }
      setDryResult(d);
    } catch (e) { setCsvError(e.message || "Validation failed."); }
    finally { setImporting(false); }
  }, []);

  const runCommit = useCallback(async () => {
    if (!csvFile) return;
    setImporting(true); setCsvError("");
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const r = await fetch(`${API_BASE}/plan-assignments/bulk-import?dry_run=false&force=true`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json();
      if (!r.ok) { setCsvError(d?.detail?.error?.message ?? d?.detail ?? `Error ${r.status}`); return; }
      setCommitted(true); setDryResult(null); setCsvFile(null);
      setToast({ type: "success", message: `${d.assignments_created ?? 0} assignment(s) created.` });
      await onAssignmentComplete();
    } catch (e) { setCsvError(e.message || "Commit failed."); }
    finally { setImporting(false); }
  }, [csvFile, onAssignmentComplete]);

  const reset = useCallback(() => { setCsvFile(null); setDryResult(null); setCommitted(false); setCsvError(""); }, []);

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]/40 p-4 space-y-3">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
            Upload <strong>PlanAssignments.csv</strong> to assign all positions in one pass. Validates before committing.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1 font-mono">
            Required: employee_id, plan_code, fiscal_period_code, business_unit_code, effective_date
          </p>
        </div>
        {csvError && (
          <div className="rounded-lg border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-3 py-2 text-tok-xs text-[rgb(var(--color-danger))]">{csvError}</div>
        )}
        {committed && (
          <div className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-success))]/20 bg-[rgb(var(--color-success))]/5 px-3 py-2 text-tok-xs text-[rgb(var(--color-success))]">
            <CheckCircle className="h-3.5 w-3.5 shrink-0" />
            Import committed successfully.
            <button onClick={reset} className="ml-auto underline text-[rgb(var(--color-text-muted))]">Import another</button>
          </div>
        )}
        {!committed && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition">
              <Upload className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
              {csvFile ? csvFile.name : "Choose file…"}
              <input type="file" accept=".csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0] ?? null; setCsvFile(f); setDryResult(null); setCommitted(false); setCsvError(""); }} />
            </label>
            <button disabled={!csvFile || importing} onClick={() => runDryRun(csvFile)}
              className="rounded-lg border border-[rgb(var(--color-primary))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/10 disabled:opacity-40 disabled:cursor-not-allowed transition">
              {importing && !dryResult ? "Validating…" : "Validate"}
            </button>
            {csvFile && <button onClick={reset} className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-danger))] transition"><X className="h-4 w-4" /></button>}
          </div>
        )}
        {dryResult && !committed && (
          <div className="space-y-2">
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs space-y-1">
              <p className="text-[rgb(var(--color-text))]">Rows parsed: <strong>{dryResult.validated}</strong> · Assignments to create: <strong>{dryResult.assignments_would_create}</strong></p>
              {dryResult.errors?.map((e, i) => (
                <p key={i} className="text-[rgb(var(--color-danger))]">Row {e.row}: {e.field} — {e.message}</p>
              ))}
            </div>
            {dryResult.errors?.length === 0 && (
              <button disabled={importing} onClick={runCommit}
                className="w-full rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-40 transition">
                {importing ? "Committing…" : `Confirm — Create ${dryResult.assignments_would_create} Assignment(s)`}
              </button>
            )}
          </div>
        )}
      </div>
      {toast && (
        <div className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 px-4 py-3 text-tok-sm font-medium text-[rgb(var(--color-success))]">
          <CheckCircle className="h-4 w-4 shrink-0" />{toast.message}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PreviewModal
// ---------------------------------------------------------------------------
function PreviewModal({ result, onBack, onConfirm, confirmLoading, positionCount, planName, periodLabel }) {
  const warnings       = result?.warnings ?? [];
  const wouldCreate    = result?.assignments_would_create ?? 0;
  const wouldSupersede = result?.assignments_would_supersede ?? 0;
  const errors         = result?.errors ?? [];
  const hasErrors      = errors.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onBack}>
      <div className="relative w-full max-w-[480px] mx-4 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl"
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="preview-modal-title">
        <div className="border-b border-[rgb(var(--color-border))] px-6 py-4">
          <p id="preview-modal-title" className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Assignment Preview</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{planName} · {periodLabel} · {positionCount} position{positionCount === 1 ? "" : "s"}</p>
        </div>
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {hasErrors && (
            <div className="space-y-1">
              {errors.map((e, i) => <div key={i} className="flex items-start gap-2 text-tok-xs text-[rgb(var(--color-danger))]"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{e.message}</span></div>)}
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-2">Resolve errors before proceeding.</p>
            </div>
          )}
          {!hasErrors && (
            <div className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-success))]">
              <CheckCircle className="h-4 w-4 shrink-0" />
              <span><strong>{wouldCreate}</strong> new assignment{wouldCreate === 1 ? "" : "s"} will be created</span>
            </div>
          )}
          {warnings.length > 0 && !hasErrors && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-warning))]">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span><strong>{wouldSupersede}</strong> existing assignment{wouldSupersede === 1 ? "" : "s"} will be superseded:</span>
              </div>
              <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden">
                <table className="w-full text-tok-xs">
                  <thead><tr className="bg-[rgb(var(--color-surface-muted))]">
                    <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Position</th>
                    <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Current Plan</th>
                    <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">New Plan</th>
                  </tr></thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {warnings.map((w, i) => <tr key={i}>
                      <td className="px-3 py-2 text-[rgb(var(--color-text))]">{w.position_id ?? `Row ${w.row}`}</td>
                      <td className="px-3 py-2 text-[rgb(var(--color-text-muted))]">{w.existing_plan_code ?? "—"}</td>
                      <td className="px-3 py-2 text-[rgb(var(--color-primary))]">{w.new_plan_code ?? "—"}</td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg border border-[rgb(var(--color-warning))]/20 bg-[rgb(var(--color-warning))]/5 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                Existing assignments will be superseded. All changes are in the audit trail. This action cannot be undone.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4">
          <button onClick={onBack}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface))] transition">
            Back
          </button>
          {!hasErrors && (
            <button onClick={onConfirm} disabled={confirmLoading}
              className="flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-5 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-40 transition">
              {confirmLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Assigning…</> : "Confirm & Assign"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
